import { forwardRef } from "react";

import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useId } from "../../hooks/useId";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { RadioProps } from "./Radio";
import { InputContainer } from "../shared";
import { Placeholder } from "./Placeholder";
import { SharedRadio } from "./SharedRadio";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedRadioStyles = createComponentStyleHook<InputProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "radio",
});

/**
 * The `Input` component properties.
 */

export interface InputProps extends RadioProps { }

/**
 * The main Radio component. This component is not exported to the outside world. This component is used inside the components that are being exported.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const { defaultValue = null, instructions, hideInstructions, hideLabel } = props;
  const { className: _className, style: _style, ...sharedRadioProps } = props;
  const { value = defaultValue } = props;

  const id = useId();

  const selected = value === true;
  const { mode, styles, StyleModeBoundary } = useResolvedRadioStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Radio,
      "radio",
      {
        selected,
      },
    ],
    testIdPrefix: "radio",
  });

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder fallback={<Placeholder />}>
        <InputContainer
          testId={baseHtmlProps["data-testid"]}
          id={id}
          instructions={instructions}
          tooltip={undefined}
          required={undefined}
          hideLabel={hideLabel}
          hideInstructions={hideInstructions}
        >
          <SharedRadio {...sharedRadioProps} id={id} ref={ref} />
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
